import { resolveProvinceMapNameFromCityCode } from './orderPrimaryPhaseGeoBuckets';
import { resolveOrderStatsBrandName } from '../../../../../utils/order/orderStatsBrand';

export const ORDER_PRIMARY_PHASE_DIMENSIONS = {
	SALE_USER: 'saleUser',
	ASSISTANT_USER: 'assistantUser',
	CUSTOMER: 'customer',
	CUSTOMER_CONTACT: 'customerContact',
	PROVINCE: 'province',
	PAYMENT_METHOD: 'paymentMethod',
	BRAND: 'brand',
	SUPPLIER: 'supplier',
	CATEGORY: 'category',
	PRODUCT_NAME: 'productName',
	PRODUCT_CODE: 'productCode',
};

export const ORDER_PRIMARY_PHASE_DIMENSION_OPTIONS = [
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER, label: '销售员' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.ASSISTANT_USER, label: '销售助理' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER, label: '单位' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER_CONTACT, label: '联系人' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.PROVINCE, label: '省份' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER, label: '厂家' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND, label: '品牌' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY, label: '品类' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME, label: '品名' },
	{ value: ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_CODE, label: '货号' },
];

/** 需按 orderItems 行展开的维度 */
export const ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS = new Set([
	ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER,
	ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY,
	ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME,
	ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_CODE,
	ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND,
]);

const normalizeCustomerId = (customer) => {
	if (customer == null || customer === '') return '';
	if (typeof customer === 'string') return customer;
	if (typeof customer === 'object') {
		if (customer._id != null) return String(customer._id);
		if (typeof customer.toString === 'function' && customer.toString() !== '[object Object]') {
			return customer.toString();
		}
	}
	return String(customer);
};

const normalizeLineItemText = (value) => String(value ?? '').trim();

export const resolveOrderLineItemAmount = (item) => {
	const price = Number(item?.sellingPrice);
	const qty = Number(item?.qty);
	if (Number.isFinite(price) && Number.isFinite(qty) && qty !== 0) return price * qty;
	if (Number.isFinite(price)) return price;
	return 0;
};

const resolveLineItemDimensionMeta = (item, dimension) => {
	let raw = '';
	let prefix = '';
	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER) {
		raw = normalizeLineItemText(item?.supplierName);
		prefix = 'supplier';
	} else if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY) {
		raw = normalizeLineItemText(item?.category);
		prefix = 'category';
	} else if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME) {
		raw = normalizeLineItemText(item?.name);
		prefix = 'productName';
	} else if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_CODE) {
		raw = normalizeLineItemText(item?.productCode);
		prefix = 'productCode';
	} else if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND) {
		raw = normalizeLineItemText(item?.brand) || resolveOrderStatsBrandName({ orderItems: [item] });
		prefix = 'brand';
	} else {
		return null;
	}

	const label =
		raw ||
		(dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER
			? '未知厂家'
			: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY
				? '未分类'
				: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME
					? '未知品名'
					: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_CODE
						? '未知货号'
						: '未分类');

	return {
		key: `${prefix}:${raw || '__unknown__'}`,
		label,
	};
};

/** 行维度：从 orderItems 解析 { key, label, amount }[]；无行金额时均分合同额；无有效行时回退整单未知桶 */
export const resolveOrderLineItemDimensionBuckets = (order, dimension) => {
	const items = Array.isArray(order?.orderItems) ? order.orderItems : [];
	const orderTotal = Number(order?.totalPrice) || 0;
	const rows = [];

	for (const item of items) {
		const meta = resolveLineItemDimensionMeta(item, dimension);
		if (!meta) continue;
		rows.push({ ...meta, amount: resolveOrderLineItemAmount(item) });
	}

	if (rows.length === 0) {
		if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.BRAND) {
			const brand = resolveOrderStatsBrandName(order);
			return [{ key: `brand:${brand || '__unknown__'}`, label: brand || '未分类', amount: orderTotal }];
		}

		const fallbackPrefix =
			dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER
				? 'supplier'
				: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY
					? 'category'
					: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME
						? 'productName'
						: 'productCode';
		const fallbackLabel =
			dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.SUPPLIER
				? '未知厂家'
				: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.CATEGORY
					? '未分类'
					: dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PRODUCT_NAME
						? '未知品名'
						: '未知货号';
		return [{ key: `${fallbackPrefix}:__unknown__`, label: fallbackLabel, amount: orderTotal }];
	}

	const amountSum = rows.reduce((sum, row) => sum + row.amount, 0);
	if (amountSum <= 0 && orderTotal > 0) {
		const share = orderTotal / rows.length;
		for (const row of rows) row.amount = share;
	}

	const byKey = new Map();
	for (const row of rows) {
		const prev = byKey.get(row.key);
		if (prev) {
			prev.amount += row.amount;
		} else {
			byKey.set(row.key, { key: row.key, label: row.label, amount: row.amount });
		}
	}

	return [...byKey.values()];
};

export const resolveOrderDimensionKey = (order, dimension) => {
	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER) {
		const id = String(order?.saleUserId ?? '').trim();
		if (id) return `saleUser:${id}`;
		const name = String(order?.saleUserName ?? '').trim();
		return `saleUser:__name__:${name || '__unassigned__'}`;
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.ASSISTANT_USER) {
		const id = String(order?.assistantUserId ?? '').trim();
		if (id) return `assistantUser:${id}`;
		const name = String(order?.assistantUserName ?? '').trim();
		return `assistantUser:__name__:${name || '__unassigned__'}`;
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER_CONTACT) {
		const id = String(order?.customerInfo?.staff_id ?? '').trim();
		if (id) return `contact:${id}`;
		const name = String(order?.customerInfo?.staffName ?? '').trim();
		return `contact:__name__:${name || '__unknown__'}`;
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PROVINCE) {
		const province = resolveProvinceMapNameFromCityCode(order?.customerCity);
		return `province:${province || '__unknown__'}`;
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PAYMENT_METHOD) {
		const method = String(order?.paymentMethod ?? '').trim();
		return `paymentMethod:${method || '__unknown__'}`;
	}

	if (ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS.has(dimension)) {
		return resolveOrderLineItemDimensionBuckets(order, dimension)[0]?.key ?? `${dimension}:__unknown__`;
	}

	const customerId = normalizeCustomerId(order?.customer);
	return `customer:${customerId || '__unknown__'}`;
};

export const resolveOrderDimensionLabel = (order, dimension) => {
	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER) {
		return String(order?.saleUserName ?? '').trim() || '未分配';
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.ASSISTANT_USER) {
		return String(order?.assistantUserName ?? '').trim() || '未分配';
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.CUSTOMER_CONTACT) {
		return String(order?.customerInfo?.staffName ?? '').trim() || '未知联系人';
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PROVINCE) {
		return resolveProvinceMapNameFromCityCode(order?.customerCity) || '未知地区';
	}

	if (dimension === ORDER_PRIMARY_PHASE_DIMENSIONS.PAYMENT_METHOD) {
		return String(order?.paymentMethod ?? '').trim() || '未知';
	}

	if (ORDER_PRIMARY_PHASE_LINE_ITEM_DIMENSIONS.has(dimension)) {
		return resolveOrderLineItemDimensionBuckets(order, dimension)[0]?.label ?? '未知';
	}

	return order?.customerInfo?.customerName?.trim() || '未知客户';
};

export const getOrderPrimaryPhaseDimensionLabel = (dimension) =>
	ORDER_PRIMARY_PHASE_DIMENSION_OPTIONS.find((item) => item.value === dimension)?.label ?? '维度';
